import { motion } from 'framer-motion'
import { Target, Eye, Award, Users } from 'lucide-react'

const values = [
    {
        icon: Target,
        title: 'Mission',
        description:
            'To bridge the critical gap between AI innovation and business value by providing a robust LLMOps platform that empowers enterprises to rapidly, securely, and cost-effectively scale complex LLM Agents.',
    },
    {
        icon: Eye,
        title: 'Vision',
        description:
            'To become the global standard for enterprise AI orchestration, enabling every organization to harness the full potential of Large Language Models and Agent-based systems.',
    },
    {
        icon: Award,
        title: 'Excellence',
        description:
            'We maintain the highest standards in engineering, security, and performance, delivering solutions that exceed expectations and drive measurable business impact.',
    },
    {
        icon: Users,
        title: 'Partnership',
        description:
            'We work closely with our clients as true partners, understanding their unique challenges and co-creating solutions that solve real business problems.',
    },
]

const timeline = [
    {
        year: '2023',
        title: 'Foundation',
        description: 'AgentForge AI was founded by a team of AI researchers and engineers from leading tech companies.',
    },
    {
        year: '2024',
        title: 'Product Launch',
        description: 'Released the AgentForge Platform with Nexus Flow Engine, Vertex Retrieval Unit, and Apex Speed Core.',
    },
    {
        year: '2025',
        title: 'Scale & Growth',
        description: 'Expanded to serve enterprise clients across multiple industries, processing billions of AI requests monthly.',
    },
]

const team = [
    {
        role: 'Engineering Excellence',
        description: 'Our team brings decades of combined experience from leading tech companies, with deep expertise in distributed systems, GPU computing, and ML infrastructure.',
    },
    {
        role: 'Research Leadership',
        description: 'Published researchers in AI/ML with contributions to major conferences (NeurIPS, ICML, ACL). We stay at the forefront of LLM and agent research.',
    },
    {
        role: 'Industry Veterans',
        description: 'Proven track record of building and scaling production AI systems serving millions of users at Fortune 500 companies.',
    },
]

export const AboutPage = () => {
    return (
        <main className="pt-20">
            {/* Hero Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 -z-10" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent -z-10" />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                            About
                            <br />
                            <span className="gradient-text">AgentForge AI</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-400 max-w-4xl mx-auto">
                            We're on a mission to democratize enterprise AI, making sophisticated
                            LLM agent systems accessible, reliable, and production-ready for every organization.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-24 bg-slate-900/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Our Mission & Values
                        </h2>
                        <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                            Guiding principles that drive everything we do
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {values.map((value, index) => {
                            const Icon = value.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700/50 hover:border-primary-500/50 transition-all duration-300"
                                >
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-6">
                                        <Icon className="w-7 h-7 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                                    <p className="text-slate-400 leading-relaxed">{value.description}</p>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            <section className="py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Journey</h2>
                        <p className="text-xl text-slate-400">
                            From startup to industry leader
                        </p>
                    </motion.div>

                    <div className="space-y-12">
                        {timeline.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="relative pl-20"
                            >
                                <div className="absolute left-0 top-0 w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center font-bold text-lg shadow-lg">
                                    {item.year}
                                </div>
                                <div className="p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
                                    <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                                    <p className="text-slate-400">{item.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-24 bg-slate-900/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Team</h2>
                        <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                            World-class expertise in AI, ML, and distributed systems
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {team.map((member, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700/50"
                            >
                                <h3 className="text-xl font-bold mb-4 gradient-text">{member.role}</h3>
                                <p className="text-slate-400 leading-relaxed">{member.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Join Us on Our Mission
                        </h2>
                        <p className="text-xl text-slate-400 mb-8">
                            We're always looking for talented individuals passionate about AI
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40">
                                View Open Positions
                            </button>
                            <button className="px-8 py-4 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 hover:border-slate-600 rounded-xl font-semibold text-lg transition-all duration-300">
                                Contact Us
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </main>
    )
}

